import { Link } from 'react-router';
import { GlassPanel } from '@/components/glass/GlassPanel';
import { cn } from '@/lib/utils';
import { formatRelativeTime, formatDuration } from '@/lib/format';
import { JobStatusBadge } from './JobStatusBadge';
import { AgentProgressGrid } from './AgentProgressGrid';
import { AGENT_NAMES, type JobListDTO } from '@/types/db';

/**
 * `<JobRowCard>` — UI-SPEC § 3.5 single card in the JobsList stream.
 *
 * Layout:
 * - md+: 3-col grid `[120px 1fr auto]` for status badge · address+meta · dots.
 * - <md: 2-row stack — top row holds status + dots; bottom row holds the
 *   address + meta line.
 *
 * Interaction:
 * - Wrapped in `<Link to="/jobs/:id">` so the entire card is the click
 *   target; hover lifts -translate-y-0.5 + extra shadow.
 * - The aggregate `aria-label` includes status + completed-count so SR users
 *   never have to traverse into the dots.
 *
 * Reduced-motion: hover translate is suppressed; the link is still visually
 * hoverable via `hover:shadow-2xl`.
 */
export interface JobRowCardProps {
  job: JobListDTO;
}

export function JobRowCard({ job }: JobRowCardProps) {
  const completedCount = AGENT_NAMES.filter(
    (n) => job.agent_progress[n] === 'completed',
  ).length;
  const ariaLabel =
    `Open job details for ${job.address}, status ${job.status}, ` +
    `${completedCount} of ${AGENT_NAMES.length} agents complete`;

  const metaParts: string[] = [];
  metaParts.push(job.job_name ? job.job_name : `Job #${job.job_id}`);
  metaParts.push(`started ${formatRelativeTime(job.started_at || job.created_at)}`);
  metaParts.push(`duration ${formatDuration(job.duration_sec)}`);

  return (
    <Link
      to={`/jobs/${job.job_id}`}
      aria-label={ariaLabel}
      className={cn(
        'block max-w-[1100px] mx-auto',
        'transition-all duration-150 ease-out',
        'hover:-translate-y-0.5 motion-reduce:hover:translate-y-0',
        'hover:shadow-2xl',
        'focus-visible:outline-2 focus-visible:outline-bronze-500/60 focus-visible:outline-offset-2 rounded-2xl',
      )}
    >
      <GlassPanel
        variant="base"
        as="article"
        className={cn(
          'min-h-[88px] p-4',
          'flex flex-col gap-3',
          'md:grid md:grid-cols-[120px_1fr_auto] md:gap-5 md:items-center md:p-5',
        )}
      >
        {/* Mobile: status + dots in top row; md+: status sits in cell 1 alone. */}
        <div className="flex items-center justify-between gap-3 md:contents">
          <JobStatusBadge status={job.status} size="sm" />
          <div className="md:hidden">
            <AgentProgressGrid agentProgress={job.agent_progress} />
          </div>
        </div>
        <div className="flex flex-col gap-1 min-w-0">
          <h2 className="text-xl font-semibold text-foam-50 truncate">
            {job.address}
          </h2>
          <p className="text-sm text-foam-50/70 truncate">
            {metaParts.join(' · ')}
          </p>
        </div>
        <div className="hidden md:block">
          <AgentProgressGrid agentProgress={job.agent_progress} />
        </div>
      </GlassPanel>
    </Link>
  );
}
